import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import {
    LogOut, Download, Search, Zap, RefreshCw, Phone, MessageSquare, Mail, Trash2, Shield,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { http, API } from "@/lib/api";
import { BRAND, BUSINESS_PHONE_FORMATTED } from "@/lib/constants";
import AppointmentsPanel from "@/components/AppointmentsPanel";
import BrandManager from "@/components/BrandManager";
import AiInsightsPanel from "@/components/AiInsightsPanel";

const STATUS_OPTIONS = ["new", "contacted", "follow_up", "qualified", "completed", "archived"];
const PRIORITY_OPTIONS = ["high", "medium", "long_term"];

function PriorityBadge({ value }) {
    const map = {
        high: "badge-high",
        medium: "badge-medium",
        long_term: "badge-long",
    };
    const label = { high: "High", medium: "Medium", long_term: "Long-term" }[value] || value;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${map[value] || "border border-white/10 text-slate-400"}`}>
            {label}
        </span>
    );
}

function Stat({ label, value, testid }) {
    return (
        <div className="card-surface p-5" data-testid={testid}>
            <div className="text-xs text-slate-400 mono uppercase tracking-wider">{label}</div>
            <div className="text-3xl font-extrabold text-white mt-1">{value}</div>
        </div>
    );
}

export default function AdminDashboard() {
    const { user, loading, logout } = useAuth();
    const [stats, setStats] = useState(null);
    const [leads, setLeads] = useState([]);
    const [search, setSearch] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterPriority, setFilterPriority] = useState("");
    const [refreshing, setRefreshing] = useState(false);
    const [selected, setSelected] = useState(null);

    const load = async () => {
        setRefreshing(true);
        try {
            const [s, l] = await Promise.all([
                http.get("/leads/stats"),
                http.get("/leads", {
                    params: {
                        search: search || undefined,
                        status: filterStatus || undefined,
                        priority: filterPriority || undefined,
                        limit: 500,
                    },
                }),
            ]);
            setStats(s.data);
            setLeads(l.data.items);
        } catch (e) {
            toast.error("Failed to load leads");
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        if (user && user.role === "admin") load();
        // eslint-disable-next-line
    }, [user, filterStatus, filterPriority]);

    const handleSearch = (e) => {
        e.preventDefault();
        load();
    };

    const updateLead = async (id, patch) => {
        try {
            const { data } = await http.patch(`/leads/${id}`, patch);
            setLeads((prev) => prev.map((l) => (l.id === id ? data : l)));
            if (selected?.id === id) setSelected(data);
            toast.success("Updated");
        } catch (e) {
            toast.error("Update failed");
        }
    };

    const removeLead = async (id) => {
        if (!window.confirm("Delete this lead permanently?")) return;
        try {
            await http.delete(`/leads/${id}`);
            setLeads((prev) => prev.filter((l) => l.id !== id));
            if (selected?.id === id) setSelected(null);
            toast.success("Lead deleted");
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const downloadCsv = () => {
        window.open(`${API}/leads/export.csv`, "_blank");
    };

    const priorityCounts = stats?.priority_counts || {};
    const statusCounts = stats?.status_counts || {};

    if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Checking session...</div>;
    if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

    return (
        <div className="min-h-screen" data-testid="admin-dashboard">
            <header className="glass sticky top-0 z-30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
                    <Link to="/" className="flex items-center gap-2">
                        <span className="w-9 h-9 rounded-xl bg-emerald-500/20 border border-emerald-500/40 inline-flex items-center justify-center text-emerald-300">
                            <Zap className="w-5 h-5" />
                        </span>
                        <span className="font-extrabold text-white text-lg">{BRAND}</span>
                        <span className="hidden sm:inline-flex ml-2 chip"><Shield className="w-3 h-3" /> Admin</span>
                    </Link>
                    <div className="ml-auto flex items-center gap-2">
                        <span className="hidden sm:inline text-xs text-slate-400 mr-2">{user.email}</span>
                        <button onClick={load} className="btn-ghost !py-2 !min-h-0 text-sm" data-testid="admin-refresh-btn">
                            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} /> Refresh
                        </button>
                        <button onClick={downloadCsv} className="btn-ghost !py-2 !min-h-0 text-sm" data-testid="admin-export-csv-btn">
                            <Download className="w-4 h-4" /> Export CSV
                        </button>
                        <button onClick={logout} className="btn-ghost !py-2 !min-h-0 text-sm" data-testid="admin-logout-btn">
                            <LogOut className="w-4 h-4" /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <Stat label="Total leads" value={stats?.total ?? "—"} testid="stat-total" />
                    <Stat label="Today" value={stats?.today ?? "—"} testid="stat-today" />
                    <Stat label="High priority" value={priorityCounts.high ?? 0} testid="stat-high" />
                    <Stat label="New" value={statusCounts.new ?? 0} testid="stat-new" />
                </div>

                <div className="mt-6 card-surface p-4 sm:p-5">
                    <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-3">
                        <div className="relative flex-1 min-w-[220px]">
                            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input
                                data-testid="admin-search-input"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name, email, phone, ZIP..."
                                className="input-dark pl-9"
                            />
                        </div>
                        <select
                            data-testid="admin-filter-status"
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            className="input-dark max-w-[180px]"
                        >
                            <option value="">All statuses</option>
                            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select
                            data-testid="admin-filter-priority"
                            value={filterPriority}
                            onChange={(e) => setFilterPriority(e.target.value)}
                            className="input-dark max-w-[180px]"
                        >
                            <option value="">All priorities</option>
                            {PRIORITY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <button className="btn-primary !py-2 !min-h-0" data-testid="admin-search-btn">Apply</button>
                    </form>
                </div>

                <div className="mt-6 grid gap-4 lg:grid-cols-5">
                    <div className="lg:col-span-3 card-surface p-0 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-white/[0.03] text-slate-400 text-xs uppercase tracking-wider">
                                    <tr>
                                        <th className="text-left px-4 py-3">When</th>
                                        <th className="text-left px-4 py-3">Lead</th>
                                        <th className="text-left px-4 py-3">Type</th>
                                        <th className="text-left px-4 py-3">ZIP</th>
                                        <th className="text-left px-4 py-3">Priority</th>
                                        <th className="text-left px-4 py-3">Status</th>
                                        <th className="text-right px-4 py-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-white/5">
                                    {leads.length === 0 && (
                                        <tr><td colSpan={7} className="text-center text-slate-500 py-12">No leads yet. Share your link.</td></tr>
                                    )}
                                    {leads.map((l) => (
                                        <tr
                                            key={l.id}
                                            data-testid={`lead-row-${l.id}`}
                                            className={`hover:bg-white/[0.03] cursor-pointer ${selected?.id === l.id ? "bg-emerald-500/5" : ""}`}
                                            onClick={() => setSelected(l)}
                                        >
                                            <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                                                {new Date(l.created_at).toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="font-semibold text-white">{l.first_name} {l.last_name}</div>
                                                <div className="text-xs text-slate-400">{l.email}</div>
                                            </td>
                                            <td className="px-4 py-3 text-slate-300">{l.customer_type}</td>
                                            <td className="px-4 py-3 text-slate-300">{l.zip_code}</td>
                                            <td className="px-4 py-3"><PriorityBadge value={l.priority} /></td>
                                            <td className="px-4 py-3 text-xs text-slate-300">{l.status}</td>
                                            <td className="px-4 py-3 text-right">
                                                <button
                                                    data-testid={`delete-lead-${l.id}`}
                                                    onClick={(e) => { e.stopPropagation(); removeLead(l.id); }}
                                                    className="text-slate-400 hover:text-red-400 inline-flex items-center gap-1 text-xs"
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="lg:col-span-2">
                        {selected ? (
                            <LeadDetail lead={selected} onChange={updateLead} />
                        ) : (
                            <div className="card-surface p-6 text-sm text-slate-400">
                                Click a lead to see details and manage status.
                            </div>
                        )}
                    </div>
                </div>

                <div className="mt-6">
                    <AiInsightsPanel />
                </div>

                <div className="mt-6">
                    <AppointmentsPanel />
                </div>

                <div className="mt-6">
                    <BrandManager />
                </div>

                <p className="mt-10 text-xs text-slate-500">
                    Need help? Call {BUSINESS_PHONE_FORMATTED}. Signed in as {user.email}.
                </p>
            </main>
        </div>
    );
}

function LeadDetail({ lead, onChange }) {
    const [notes, setNotes] = useState(lead.admin_notes || "");
    useEffect(() => setNotes(lead.admin_notes || ""), [lead.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const info = useMemo(() => ([
        ["Phone", lead.phone],
        ["Preferred", lead.preferred_contact],
        ["Service", lead.service_of_interest],
        ["Referral code", lead.referral_code],
        ["Referred by", lead.referred_by_name || (lead.referred_by_code ? `(code ${lead.referred_by_code})` : null)],
        ["Friends referred", lead.referrals_count ? String(lead.referrals_count) : null],
        ["State", lead.state],
        ["Utility", lead.utility_territory],
        ["Situation", lead.situation],
        ["Usage", lead.monthly_usage],
        ["Business", lead.business_type],
        ["Locations", lead.number_of_locations],
        ["Move date", lead.move_date],
        ["Source", lead.source],
        ["UTM", [lead.utm_source, lead.utm_medium, lead.utm_campaign].filter(Boolean).join(" / ")],
        ["Consent", lead.consent ? "Yes" : "No"],
    ].filter(([_, v]) => v)), [lead]);

    return (
        <div className="card-surface p-5" data-testid="lead-detail-panel">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="text-xs text-slate-400 mono">#{lead.id.slice(-6)}</div>
                    <h3 className="text-xl font-semibold text-white">{lead.first_name} {lead.last_name}</h3>
                    <div className="text-sm text-slate-400">{lead.email}</div>
                </div>
                <PriorityBadge value={lead.priority} />
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
                <a href={`tel:${lead.phone}`} className="btn-ghost !py-2 !min-h-0 text-xs"><Phone className="w-3.5 h-3.5" /> Call</a>
                <a href={`sms:${lead.phone}`} className="btn-ghost !py-2 !min-h-0 text-xs"><MessageSquare className="w-3.5 h-3.5" /> Text</a>
                <a href={`mailto:${lead.email}`} className="btn-ghost !py-2 !min-h-0 text-xs"><Mail className="w-3.5 h-3.5" /> Email</a>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                {info.map(([k, v]) => (
                    <div key={k} className="flex flex-col">
                        <span className="text-slate-500">{k}</span>
                        <span className="text-slate-200 break-words">{String(v)}</span>
                    </div>
                ))}
            </div>

            <div className="mt-5 grid grid-cols-2 gap-2">
                <div>
                    <label className="text-xs text-slate-400">Status</label>
                    <select
                        data-testid="detail-status-select"
                        value={lead.status}
                        onChange={(e) => onChange(lead.id, { status: e.target.value })}
                        className="input-dark mt-1 text-sm"
                    >
                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-xs text-slate-400">Priority</label>
                    <select
                        data-testid="detail-priority-select"
                        value={lead.priority}
                        onChange={(e) => onChange(lead.id, { priority: e.target.value })}
                        className="input-dark mt-1 text-sm"
                    >
                        {PRIORITY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
            </div>

            <div className="mt-4">
                <label className="text-xs text-slate-400">Private admin notes</label>
                <textarea
                    data-testid="detail-notes-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    onBlur={() => notes !== (lead.admin_notes || "") && onChange(lead.id, { admin_notes: notes })}
                    className="input-dark mt-1 min-h-[80px] text-sm"
                    placeholder="Follow-up notes..."
                />
            </div>
        </div>
    );
}
